import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/AdminLayout';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Skeleton,
  Tabs,
  Tab,
  alpha,
  LinearProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Download,
  InsertDriveFile,
  PictureAsPdf,
  FiberManualRecord,
  Refresh,
  ShoppingCart,
  Euro,
  Speed,
  LocalShipping,
  CreditCard,
  Payments,
  Store,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip as RechartsTooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
  Area,
  AreaChart,
} from 'recharts';
import exportService from '../../services/exportService';
import {
  getBusinessDayBounds,
  parseOrderDate,
  parseOrderTotal,
  isRevenueOrder,
  countOrdersItems,
} from '../../utils/reportingUtils';

interface RevenueData {
  total: number;
  orders: number;
  avgOrder: number;
  growth?: number;
  ordersGrowth?: number;
  avgGrowth?: number;
  // German VAT breakdown
  netRevenue?: number;       // Net (without VAT)
  taxCollected?: number;     // Total VAT
  netDineIn?: number;        // Dine-in net (19% VAT base)
  taxDineIn?: number;        // Dine-in VAT (19%)
  netTakeaway?: number;      // Delivery + Pickup net (7% VAT base)
  taxTakeaway?: number;      // Delivery + Pickup VAT (7%)
  grossDineIn?: number;      // Dine-in gross
  grossTakeaway?: number;    // Takeaway gross
}

// Compute VAT split per German law: dine-in 19%, takeaway/delivery 7%.
// Returns RAW floats — display call sites must format with toFixed(2).
// Rounding components separately and then summing them produced ±€0.01
// drift that auditors flagged. Round once, at the leaf where the value is
// rendered, never in the middle of arithmetic.
function computeVAT(orders: any[]) {
  let netDineIn = 0, taxDineIn = 0, grossDineIn = 0;
  let netTakeaway = 0, taxTakeaway = 0, grossTakeaway = 0;
  orders.forEach(o => {
    const total = typeof o.total === 'number' ? o.total : parseFloat(o.total || '0');
    if (isNaN(total)) return;
    const orderType = (o.order_type || '').toLowerCase();
    const isDineIn = orderType === 'dine_in' || orderType === 'dinein';
    const vatRate = isDineIn ? 0.19 : 0.07;
    const net = total / (1 + vatRate);
    const tax = total - net;
    if (isDineIn) { netDineIn += net; taxDineIn += tax; grossDineIn += total; }
    else { netTakeaway += net; taxTakeaway += tax; grossTakeaway += total; }
  });
  return {
    netRevenue: netDineIn + netTakeaway,
    taxCollected: taxDineIn + taxTakeaway,
    netDineIn,
    taxDineIn,
    netTakeaway,
    taxTakeaway,
    grossDineIn,
    grossTakeaway,
  };
}

interface ReportsData {
  today: RevenueData;
  week: RevenueData;
  month: RevenueData;
}

// Chart color palette
const COLORS = ['#2E7D32', '#43A047', '#66BB6A', '#81C784', '#A5D6A7', '#C8E6C9', '#388E3C', '#1B5E20'];
const STATUS_COLORS: Record<string, string> = {
  completed: '#2E7D32',
  delivered: '#43A047',
  ready: '#66BB6A',
  preparing: '#FF9800',
  confirmed: '#1976D2',
  pending: '#9E9E9E',
  cancelled: '#d32f2f',
};

const ReportsPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [period, setPeriod] = useState<'today' | 'week' | 'month'>('week');
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [revenueData, setRevenueData] = useState<ReportsData>({
    today: { total: 0, orders: 0, avgOrder: 0 },
    week: { total: 0, orders: 0, avgOrder: 0 },
    month: { total: 0, orders: 0, avgOrder: 0 },
  });

  const [salesTrendData, setSalesTrendData] = useState<any[]>([]);
  const [categoryData, setCategoryData] = useState<any[]>([]);
  const [branchData, setBranchData] = useState<any[]>([]);
  const [topItems, setTopItems] = useState<any[]>([]);
  const [orderStatusData, setOrderStatusData] = useState<any[]>([]);
  const [paymentMethodData, setPaymentMethodData] = useState<any[]>([]);
  const [recentOrders, setRecentOrders] = useState<any[]>([]);
  const [todayOrders, setTodayOrders] = useState<any[]>([]);
  const [weekOrders, setWeekOrders] = useState<any[]>([]);
  const [monthOrders, setMonthOrders] = useState<any[]>([]);
  const [deliveryVsPickup, setDeliveryVsPickup] = useState<any[]>([]);
  const [peakHoursData, setPeakHoursData] = useState<any[]>([]);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    const fetchReportsData = async () => {
      setLoading(true);
      setError(null);

      try {
        const { collection, onSnapshot, query, orderBy } = await import('../../services/firestoreCompat');
        const { db } = await import('../../services/firebaseConfig');

        const ordersRef = collection(db, 'orders');
        const ordersQuery = query(ordersRef, orderBy('created_at', 'desc'));

        unsubscribe = onSnapshot(ordersQuery, (ordersSnapshot) => {
          try {
            // Shared business-day boundary so Reports reconciles with POS Orders + Branches.
            const now = new Date();
            const today = getBusinessDayBounds('today').start;
            const weekAgo = getBusinessDayBounds('week').start;
            const monthAgo = getBusinessDayBounds('month').start;

            const allOrders: any[] = [];
            ordersSnapshot.forEach((doc) => {
              const data = doc.data();
              const orderDate = parseOrderDate(data);
              if (!orderDate) return;

              const orderTotal = parseOrderTotal(data);
              allOrders.push({
                ...data,
                created_at: orderDate,
                total: orderTotal,
                id: doc.id,
                status: (data.status || 'pending').toLowerCase(),
                payment_status: data.payment_status || 'completed',
                payment_method: data.payment_method || data.paymentMethod || 'cash',
                order_type: data.order_type || data.orderType || data.type || 'pickup',
              });
            });

            // Valid orders: shared revenue filter (excludes cancelled/refunded/voided/failed
            // and any order fully refunded by amount). Single source of truth in reportingUtils.ts.
            const validOrders = allOrders.filter(isRevenueOrder);

            // Filter by period
            const todayFiltered = validOrders.filter(o => o.created_at >= today);
            const weekFiltered = validOrders.filter(o => o.created_at >= weekAgo);
            const monthFiltered = validOrders.filter(o => o.created_at >= monthAgo);

            setTodayOrders(todayFiltered);
            setWeekOrders(weekFiltered);
            setMonthOrders(monthFiltered);

            // Previous period for growth
            const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
            const lastWeekStart = new Date(weekAgo); lastWeekStart.setDate(lastWeekStart.getDate() - 7);
            const lastMonthStart = new Date(monthAgo); lastMonthStart.setDate(lastMonthStart.getDate() - 30);

            const yesterdayOrders = validOrders.filter(o => o.created_at >= yesterday && o.created_at < today);
            const lastWeekOrders = validOrders.filter(o => o.created_at >= lastWeekStart && o.created_at < weekAgo);
            const lastMonthOrders = validOrders.filter(o => o.created_at >= lastMonthStart && o.created_at < monthAgo);

            const calcGrowth = (cur: number, prev: number) => prev === 0 ? (cur > 0 ? 100 : 0) : ((cur - prev) / prev) * 100;
            const calcRev = (orders: any[]) => orders.reduce((s, o) => s + (o.total || 0), 0);

            const todayRev = calcRev(todayFiltered);
            const weekRev = calcRev(weekFiltered);
            const monthRev = calcRev(monthFiltered);
            const yesterdayRev = calcRev(yesterdayOrders);
            const lastWeekRev = calcRev(lastWeekOrders);
            const lastMonthRev = calcRev(lastMonthOrders);

            const todayVat = computeVAT(todayFiltered);
            const weekVat = computeVAT(weekFiltered);
            const monthVat = computeVAT(monthFiltered);

            setRevenueData({
              today: {
                total: todayRev, orders: todayFiltered.length,
                avgOrder: todayFiltered.length > 0 ? todayRev / todayFiltered.length : 0,
                growth: calcGrowth(todayRev, yesterdayRev),
                ordersGrowth: calcGrowth(todayFiltered.length, yesterdayOrders.length),
                avgGrowth: calcGrowth(todayFiltered.length > 0 ? todayRev / todayFiltered.length : 0, yesterdayOrders.length > 0 ? yesterdayRev / yesterdayOrders.length : 0),
                ...todayVat,
              },
              week: {
                total: weekRev, orders: weekFiltered.length,
                avgOrder: weekFiltered.length > 0 ? weekRev / weekFiltered.length : 0,
                growth: calcGrowth(weekRev, lastWeekRev),
                ordersGrowth: calcGrowth(weekFiltered.length, lastWeekOrders.length),
                avgGrowth: calcGrowth(weekFiltered.length > 0 ? weekRev / weekFiltered.length : 0, lastWeekOrders.length > 0 ? lastWeekRev / lastWeekOrders.length : 0),
                ...weekVat,
              },
              month: {
                total: monthRev, orders: monthFiltered.length,
                avgOrder: monthFiltered.length > 0 ? monthRev / monthFiltered.length : 0,
                growth: calcGrowth(monthRev, lastMonthRev),
                ordersGrowth: calcGrowth(monthFiltered.length, lastMonthOrders.length),
                avgGrowth: calcGrowth(monthFiltered.length > 0 ? monthRev / monthFiltered.length : 0, lastMonthOrders.length > 0 ? lastMonthRev / lastMonthOrders.length : 0),
                ...monthVat,
              },
            });

            // Sales trend
            const ordersForPeriod = period === 'today' ? todayFiltered : period === 'week' ? weekFiltered : monthFiltered;
            let trendData: any[] = [];

            if (period === 'today') {
              const hourlyMap = new Map<number, { revenue: number; orders: number }>();
              ordersForPeriod.forEach(o => {
                const h = o.created_at.getHours();
                const cur = hourlyMap.get(h) || { revenue: 0, orders: 0 };
                cur.revenue += o.total || 0; cur.orders += 1;
                hourlyMap.set(h, cur);
              });
              trendData = Array.from({ length: 24 }, (_, i) => {
                const s = hourlyMap.get(i) || { revenue: 0, orders: 0 };
                return { day: `${i.toString().padStart(2, '0')}:00`, revenue: +s.revenue.toFixed(2), orders: s.orders };
              });
            } else if (period === 'week') {
              const dailyMap = new Map<string, { revenue: number; orders: number }>();
              ordersForPeriod.forEach(o => {
                const k = o.created_at.toISOString().split('T')[0];
                const cur = dailyMap.get(k) || { revenue: 0, orders: 0 };
                cur.revenue += o.total || 0; cur.orders += 1;
                dailyMap.set(k, cur);
              });
              const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
              trendData = Array.from({ length: 7 }, (_, i) => {
                const d = new Date(now); d.setDate(d.getDate() - (6 - i)); d.setHours(0, 0, 0, 0);
                const k = d.toISOString().split('T')[0];
                const s = dailyMap.get(k) || { revenue: 0, orders: 0 };
                return { day: dayNames[d.getDay()], revenue: +s.revenue.toFixed(2), orders: s.orders };
              });
            } else {
              const weeklyMap = new Map<number, { revenue: number; orders: number }>();
              ordersForPeriod.forEach(o => {
                const wk = Math.min(Math.max(Math.floor((o.created_at.getTime() - monthAgo.getTime()) / (7 * 86400000)) + 1, 1), 4);
                const cur = weeklyMap.get(wk) || { revenue: 0, orders: 0 };
                cur.revenue += o.total || 0; cur.orders += 1;
                weeklyMap.set(wk, cur);
              });
              trendData = Array.from({ length: 4 }, (_, i) => {
                const s = weeklyMap.get(i + 1) || { revenue: 0, orders: 0 };
                return { day: `Week ${i + 1}`, revenue: +s.revenue.toFixed(2), orders: s.orders };
              });
            }
            setSalesTrendData(trendData);

            // Category data from ALL valid orders
            const categoryMap = new Map<string, number>();
            validOrders.forEach(o => {
              if (o.items && Array.isArray(o.items)) {
                o.items.forEach((item: any) => {
                  const cat = item.category || item.category_name || item.categoryName || 'Other';
                  const price = parseFloat(item.price || item.price_with_tax || item.unit_price || 0);
                  const qty = parseFloat(item.quantity || 1);
                  categoryMap.set(cat, (categoryMap.get(cat) || 0) + price * qty);
                });
              }
            });
            const catArr = Array.from(categoryMap.entries())
              .map(([name, sales], i) => ({ name, sales: +sales.toFixed(2), color: COLORS[i % COLORS.length] }))
              .sort((a, b) => b.sales - a.sales).slice(0, 6);
            setCategoryData(catArr.length > 0 ? catArr : []);

            // Top-selling items
            const itemMap = new Map<string, { name: string; qty: number; revenue: number; category: string }>();
            ordersForPeriod.forEach(o => {
              if (o.items && Array.isArray(o.items)) {
                o.items.forEach((item: any) => {
                  const name = item.name || item.item_name || 'Unknown Item';
                  const price = parseFloat(item.price || item.price_with_tax || item.unit_price || 0);
                  const qty = parseFloat(item.quantity || 1);
                  const cur = itemMap.get(name) || { name, qty: 0, revenue: 0, category: item.category || 'Other' };
                  cur.qty += qty; cur.revenue += price * qty;
                  itemMap.set(name, cur);
                });
              }
            });
            const topItemsArr = Array.from(itemMap.values())
              .sort((a, b) => b.revenue - a.revenue).slice(0, 8);
            setTopItems(topItemsArr);

            // Order status breakdown (from ALL orders in period, including cancelled)
            const statusMap = new Map<string, number>();
            const allOrdersInPeriod = allOrders.filter(o => {
              if (period === 'today') return o.created_at >= today;
              if (period === 'week') return o.created_at >= weekAgo;
              return o.created_at >= monthAgo;
            });
            allOrdersInPeriod.forEach(o => {
              const s = o.status || 'pending';
              statusMap.set(s, (statusMap.get(s) || 0) + 1);
            });
            const statusArr = Array.from(statusMap.entries())
              .map(([status, count]) => ({
                name: status.charAt(0).toUpperCase() + status.slice(1),
                value: count,
                color: STATUS_COLORS[status] || '#757575',
              }))
              .sort((a, b) => b.value - a.value);
            setOrderStatusData(statusArr);

            // Payment methods
            const payMap = new Map<string, { count: number; total: number }>();
            ordersForPeriod.forEach(o => {
              const pm = (o.payment_method || 'cash').toLowerCase();
              const label = pm === 'card' || pm === 'credit_card' || pm === 'debit_card' ? 'Card'
                : pm === 'cash' ? 'Cash'
                : pm === 'online' || pm === 'paypal' ? 'Online'
                : pm.charAt(0).toUpperCase() + pm.slice(1);
              const cur = payMap.get(label) || { count: 0, total: 0 };
              cur.count += 1; cur.total += o.total || 0;
              payMap.set(label, cur);
            });
            const payArr = Array.from(payMap.entries())
              .map(([method, data]) => ({ method, count: data.count, total: +data.total.toFixed(2) }))
              .sort((a, b) => b.total - a.total);
            setPaymentMethodData(payArr);

            // Delivery vs Pickup
            const typeMap = new Map<string, number>();
            ordersForPeriod.forEach(o => {
              const t = (o.order_type || 'pickup').toLowerCase();
              const label = t.includes('deliver') ? 'Delivery' : t.includes('dine') ? 'Dine-in' : 'Pickup';
              typeMap.set(label, (typeMap.get(label) || 0) + 1);
            });
            const typeArr = Array.from(typeMap.entries())
              .map(([name, value], i) => ({ name, value, color: COLORS[i % COLORS.length] }));
            setDeliveryVsPickup(typeArr);

            // Peak hours (from period orders)
            const hourMap = new Map<number, number>();
            ordersForPeriod.forEach(o => {
              const h = o.created_at.getHours();
              hourMap.set(h, (hourMap.get(h) || 0) + 1);
            });
            const peakArr = Array.from({ length: 24 }, (_, i) => ({
              hour: `${i.toString().padStart(2, '0')}:00`,
              orders: hourMap.get(i) || 0,
            })).filter(h => h.orders > 0 || (h.hour >= '08:00' && h.hour <= '23:00'));
            setPeakHoursData(peakArr);

            // Branch data
            const branchMap = new Map<string, { orders: number; revenue: number }>();
            validOrders.forEach(o => {
              const b = o.branch || o.branch_name || o.branch_id || 'Main Branch';
              const cur = branchMap.get(b) || { orders: 0, revenue: 0 };
              cur.orders += 1; cur.revenue += o.total || 0;
              branchMap.set(b, cur);
            });
            const branchArr = Array.from(branchMap.entries())
              .map(([branch, s]) => ({ branch, orders: s.orders, revenue: +s.revenue.toFixed(2) }))
              .sort((a, b) => b.revenue - a.revenue);
            setBranchData(branchArr);

            // Recent orders
            const recent = ordersForPeriod.slice(0, 10).map(o => ({
              id: o.order_number || o.id?.slice(0, 8) || '—',
              total: o.total,
              status: o.status,
              type: o.order_type,
              payment: o.payment_method,
              time: o.created_at,
              items: o.items?.length || 0,
            }));
            setRecentOrders(recent);

            setLoading(false);
            setError(null);
          } catch (err) {
            console.error('[REPORTS] Error processing snapshot:', err);
            setError('Failed to process reports data.');
            setLoading(false);
          }
        }, (err) => {
          console.error('[REPORTS] Listener error:', err);
          setError('Failed to load reports data.');
          setLoading(false);
        });
      } catch (err) {
        console.error('[REPORTS] Init error:', err);
        setError('Failed to initialize reports.');
        setLoading(false);
      }
    };

    fetchReportsData();
    return () => { if (unsubscribe) unsubscribe(); };
  }, [period]);

  const currentData = revenueData[period];
  const periodLabel = period === 'today' ? t('reports.today') : period === 'week' ? t('reports.thisWeek') : t('reports.thisMonth');
  const prevLabel = period === 'today' ? t('reports.vsYesterday') : period === 'week' ? t('reports.vsLastWeek') : t('reports.vsLastMonth');
  const ordersForCurrentPeriod = period === 'today' ? todayOrders : period === 'week' ? weekOrders : monthOrders;
  // Items Sold = sum of item.quantity across EVERY valid order in the period
  // (not the top-8 slice — that under-counted and made it appear lower than total orders).
  const itemsSoldCount = countOrdersItems(ordersForCurrentPeriod);

  const handleExportToExcel = async () => {
    try {
      setExportMenuAnchor(null);
      const ordersForPeriod = period === 'today' ? todayOrders : period === 'week' ? weekOrders : monthOrders;
      const { collection, getDocs } = await import('../../services/firestoreCompat');
      const { db } = await import('../../services/firebaseConfig');
      const menuSnapshot = await getDocs(collection(db, 'menu_items'));
      const menuItems = menuSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      await exportService.exportToExcel({ orders: ordersForPeriod, menuItems, revenueData: currentData, period: periodLabel });
    } catch (err) {
      alert('Failed to export to Excel. Please try again.');
    }
  };

  const handleExportDsfinvk = async () => {
    try {
      setExportMenuAnchor(null);
      // Compute YYYY-MM-DD bounds for the selected period
      const today = new Date();
      const fmt = (d: Date) => d.toISOString().slice(0, 10);
      let from: string;
      const to = fmt(today);
      if (period === 'today') from = to;
      else if (period === 'week') from = fmt(new Date(today.getTime() - 7 * 86400_000));
      else from = fmt(new Date(today.getTime() - 30 * 86400_000));

      const { httpsCallable, getFunctions } = await import('firebase/functions');
      const callable = httpsCallable(getFunctions(), 'exportDsfinvk');
      const res: any = await callable({ from, to });
      const { files, meta } = res.data || res;
      if (!files) throw new Error('Empty DSFinV-K response');

      const JSZip = (await import('jszip')).default;
      const { saveAs } = await import('file-saver');
      const zip = new JSZip();
      for (const [name, content] of Object.entries(files)) {
        zip.file(name, content as string);
      }
      zip.file('README.txt',
        `DSFinV-K Export — VERSION 1\n` +
        `Generated: ${meta.generated_at}\n` +
        `Period: ${meta.from} to ${meta.to}\n` +
        `Orders: ${meta.order_count}\n\n` +
        `IMPORTANT: ${meta.warning}\n`);
      const blob = await zip.generateAsync({ type: 'blob' });
      saveAs(blob, `DSFinV-K_${from}_to_${to}.zip`);
    } catch (err: any) {
      console.error('[DSFINVK] Export failed:', err);
      alert(`DSFinV-K export failed: ${err?.message || err}`);
    }
  };

  const handleExportToPDF = async () => {
    try {
      setExportMenuAnchor(null);
      const ordersForPeriod = period === 'today' ? todayOrders : period === 'week' ? weekOrders : monthOrders;
      const { collection, getDocs } = await import('../../services/firestoreCompat');
      const { db } = await import('../../services/firebaseConfig');
      const menuSnapshot = await getDocs(collection(db, 'menu_items'));
      const menuItems = menuSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      await exportService.exportToPDF({ orders: ordersForPeriod, menuItems, revenueData: currentData, period: periodLabel });
    } catch (err) {
      alert('Failed to export to PDF. Please try again.');
    }
  };

  const GrowthChip = ({ value, label }: { value?: number; label: string }) => {
    const v = value || 0;
    const isPositive = v >= 0;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        {isPositive ? <TrendingUp sx={{ fontSize: { xs: 12, md: 14 }, color: '#4CAF50' }} /> : <TrendingDown sx={{ fontSize: { xs: 12, md: 14 }, color: '#F44336' }} />}
        <Typography sx={{ fontWeight: 600, color: isPositive ? '#4CAF50' : '#F44336', fontSize: { xs: '0.62rem', md: '0.75rem' } }}>
          {isPositive ? '+' : ''}{v.toFixed(1)}%
        </Typography>
        <Typography sx={{ color: '#6b7280', fontSize: { xs: '0.55rem', md: '0.7rem' } }}>{label}</Typography>
      </Box>
    );
  };

  const renderSkeleton = () => (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2.5 }}>
        <Skeleton width={200} height={32} />
        <Skeleton width={120} height={36} sx={{ borderRadius: 2 }} />
      </Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 2.5 }}>
        {[1, 2, 3].map(i => <Skeleton key={i} width={80} height={32} sx={{ borderRadius: 2 }} />)}
      </Box>
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {[1, 2, 3, 4].map(i => (
          <Grid item xs={6} md={3} key={i}><Skeleton height={100} sx={{ borderRadius: 2 }} /></Grid>
        ))}
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}><Skeleton height={340} sx={{ borderRadius: 2 }} /></Grid>
        <Grid item xs={12} md={4}><Skeleton height={340} sx={{ borderRadius: 2 }} /></Grid>
      </Grid>
    </Box>
  );

  if (loading && salesTrendData.length === 0) {
    return <AdminLayout>{renderSkeleton()}</AdminLayout>;
  }

  return (
    <AdminLayout>
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#e0e0e0', letterSpacing: '-0.02em' }}>
              {t('reports.title')}
            </Typography>
            <Typography variant="body2" sx={{ color: '#9ca3af', mt: 0.25 }}>
              {t('reports.subtitle')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Download sx={{ fontSize: 16 }} />}
              onClick={(e) => setExportMenuAnchor(e.currentTarget)}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, borderColor: '#3a3d52', color: '#e0e0e0', fontSize: '0.8rem' }}
            >
              {t('reports.export')}
            </Button>
            <Menu
              anchorEl={exportMenuAnchor}
              open={Boolean(exportMenuAnchor)}
              onClose={() => setExportMenuAnchor(null)}
              PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 4px 16px rgba(0,0,0,0.1)', minWidth: 180 } }}
            >
              <MenuItem onClick={handleExportToExcel} sx={{ gap: 1.5 }}>
                <InsertDriveFile sx={{ fontSize: 18, color: '#2E7D32' }} />
                <ListItemText primary={t('reports.exportCSV')} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 500 }} />
              </MenuItem>
              <MenuItem onClick={handleExportToPDF} sx={{ gap: 1.5 }}>
                <PictureAsPdf sx={{ fontSize: 18, color: '#d32f2f' }} />
                <ListItemText primary={t('reports.exportPDF')} primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 500 }} />
              </MenuItem>
              <MenuItem onClick={handleExportDsfinvk} sx={{ gap: 1.5 }}>
                <InsertDriveFile sx={{ fontSize: 18, color: '#1565c0' }} />
                <ListItemText
                  primary="DSFinV-K (Finanzamt) — v1"
                  secondary="Steuerberater review required"
                  primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 500 }}
                  secondaryTypographyProps={{ fontSize: '0.7rem', color: '#9ca3af' }}
                />
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Period Tabs */}
        <Box sx={{ mb: 2.5 }}>
          <Tabs
            value={period}
            onChange={(_, v) => setPeriod(v)}
            sx={{
              minHeight: 36,
              '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', py: 0, px: 2, minWidth: 'auto' },
              '& .MuiTabs-indicator': { height: 2.5, borderRadius: 2 },
            }}
          >
            <Tab label={t('reports.today')} value="today" />
            <Tab label={t('reports.thisWeek')} value="week" />
            <Tab label={t('reports.thisMonth')} value="month" />
          </Tabs>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

        {/* KPI Cards */}
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          {[
            { label: t('reports.totalRevenue'), value: `€${currentData.total.toFixed(2)}`, growth: currentData.growth, icon: <Euro sx={{ fontSize: 20 }} />, color: '#2E7D32' },
            { label: t('reports.totalOrders'), value: currentData.orders.toLocaleString(), growth: currentData.ordersGrowth, icon: <ShoppingCart sx={{ fontSize: 20 }} />, color: '#1565C0' },
            { label: t('reports.avgOrderValue'), value: `€${currentData.avgOrder.toFixed(2)}`, growth: currentData.avgGrowth, icon: <Speed sx={{ fontSize: 20 }} />, color: '#E65100' },
            { label: t('reports.itemsSold'), value: itemsSoldCount.toLocaleString(), icon: <Store sx={{ fontSize: 20 }} />, color: '#6A1B9A' },
          ].map((kpi, idx) => (
            <Grid item xs={6} md={3} key={idx}>
              <Paper sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2.5, border: '1px solid #3a3d52', boxShadow: 'none', height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, md: 1 }, mb: { xs: 0.5, md: 1 } }}>
                  <Box sx={{ width: { xs: 26, md: 32 }, height: { xs: 26, md: 32 }, borderRadius: 1.5, bgcolor: alpha(kpi.color, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center', color: kpi.color, flexShrink: 0, '& .MuiSvgIcon-root': { fontSize: { xs: 16, md: 20 } } }}>
                    {kpi.icon}
                  </Box>
                  <Typography sx={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.02em', fontSize: { xs: '0.58rem', md: '0.7rem' }, lineHeight: 1.2 }}>
                    {kpi.label}
                  </Typography>
                </Box>
                <Typography sx={{ fontWeight: 700, color: '#e0e0e0', mb: 0.3, fontSize: { xs: '1.05rem', md: '1.5rem' }, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                  {kpi.value}
                </Typography>
                {kpi.growth !== undefined && <GrowthChip value={kpi.growth} label={prevLabel} />}
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* German VAT Breakdown — Critical for accurate tax reporting */}
        <Paper sx={{ p: { xs: 1.5, md: 2.5 }, borderRadius: 2.5, border: '1px solid #3a3d52', boxShadow: 'none', mb: 2.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#e0e0e0', mb: 2 }}>
            🧾 MwSt / VAT Breakdown — {periodLabel}
          </Typography>
          <Grid container spacing={2}>
            {/* Net Revenue */}
            <Grid item xs={12} md={3}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha('#00C896', 0.06), border: '1px solid rgba(0, 200, 150,0.2)' }}>
                <Typography sx={{ color: '#9ca3af', fontSize: '0.7rem', fontWeight: 600, mb: 0.5 }}>NET REVENUE (Netto)</Typography>
                <Typography sx={{ color: '#00C896', fontSize: '1.3rem', fontWeight: 700 }}>€{(currentData.netRevenue || 0).toFixed(2)}</Typography>
              </Box>
            </Grid>
            {/* Total VAT */}
            <Grid item xs={12} md={3}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha('#FF9800', 0.06), border: '1px solid rgba(255,152,0,0.2)' }}>
                <Typography sx={{ color: '#9ca3af', fontSize: '0.7rem', fontWeight: 600, mb: 0.5 }}>VAT COLLECTED (MwSt)</Typography>
                <Typography sx={{ color: '#FF9800', fontSize: '1.3rem', fontWeight: 700 }}>€{(currentData.taxCollected || 0).toFixed(2)}</Typography>
              </Box>
            </Grid>
            {/* Dine-in 19% */}
            <Grid item xs={12} md={3}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha('#1976D2', 0.06), border: '1px solid rgba(25,118,210,0.2)' }}>
                <Typography sx={{ color: '#9ca3af', fontSize: '0.7rem', fontWeight: 600, mb: 0.5 }}>DINE-IN (19% MwSt)</Typography>
                <Typography sx={{ color: '#42A5F5', fontSize: '1.05rem', fontWeight: 700 }}>€{(currentData.grossDineIn || 0).toFixed(2)}</Typography>
                <Typography sx={{ color: '#9ca3af', fontSize: '0.7rem', mt: 0.3 }}>
                  Net: €{(currentData.netDineIn || 0).toFixed(2)} · Tax: €{(currentData.taxDineIn || 0).toFixed(2)}
                </Typography>
              </Box>
            </Grid>
            {/* Takeaway 7% */}
            <Grid item xs={12} md={3}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha('#9C27B0', 0.06), border: '1px solid rgba(156,39,176,0.2)' }}>
                <Typography sx={{ color: '#9ca3af', fontSize: '0.7rem', fontWeight: 600, mb: 0.5 }}>TAKEAWAY/DELIVERY (7% MwSt)</Typography>
                <Typography sx={{ color: '#BA68C8', fontSize: '1.05rem', fontWeight: 700 }}>€{(currentData.grossTakeaway || 0).toFixed(2)}</Typography>
                <Typography sx={{ color: '#9ca3af', fontSize: '0.7rem', mt: 0.3 }}>
                  Net: €{(currentData.netTakeaway || 0).toFixed(2)} · Tax: €{(currentData.taxTakeaway || 0).toFixed(2)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Revenue Trend + Category Breakdown */}
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #3a3d52', boxShadow: 'none' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#e0e0e0', mb: 2 }}>
                {t('reports.revenueTrend')} — {periodLabel}
              </Typography>
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={salesTrendData}>
                  <defs>
                    <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2E7D32" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#2E7D32" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#3a3d52" />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#999' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#999' }} axisLine={false} tickLine={false} />
                  <RechartsTooltip
                    contentStyle={{ borderRadius: 8, border: '1px solid #3a3d52', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', fontSize: 12 }}
                    formatter={(value: any, name: string) => [name === 'revenue' ? `€${value}` : value, name === 'revenue' ? 'Revenue' : 'Orders']}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="#2E7D32" strokeWidth={2.5} fill="url(#revGrad)" name="revenue" />
                  <Line type="monotone" dataKey="orders" stroke="#1565C0" strokeWidth={2} dot={false} name="orders" />
                </AreaChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #3a3d52', boxShadow: 'none', height: '100%' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#e0e0e0', mb: 2 }}>
                {t('reports.categoryBreakdown')}
              </Typography>
              {categoryData.length === 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 250 }}>
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>{t('reports.noCategoryData')}</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={categoryData} cx="50%" cy="45%" outerRadius={80} innerRadius={45} dataKey="sales" paddingAngle={2}>
                      {categoryData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <RechartsTooltip
                      contentStyle={{ borderRadius: 8, border: '1px solid #3a3d52', fontSize: 12 }}
                      formatter={(value: any) => [`€${value}`, 'Revenue']}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Top Items + Order Status + Payment Methods */}
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          {/* Top Selling Items */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #3a3d52', boxShadow: 'none', height: '100%' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#e0e0e0', mb: 2 }}>
                {t('reports.topSellingItems')}
              </Typography>
              {topItems.length === 0 ? (
                <Typography variant="body2" sx={{ color: '#6b7280', py: 4, textAlign: 'center' }}>{t('reports.noItemData')}</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {topItems.map((item, i) => {
                    const maxRev = topItems[0]?.revenue || 1;
                    return (
                      <Box key={i}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                            <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 700, width: 16, textAlign: 'right' }}>
                              {i + 1}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#e0e0e0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {item.name}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right', flexShrink: 0, ml: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#2E7D32' }}>
                              €{item.revenue.toFixed(2)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', fontSize: '0.65rem' }}>
                              {item.qty}x sold
                            </Typography>
                          </Box>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={(item.revenue / maxRev) * 100}
                          sx={{ height: 4, borderRadius: 2, bgcolor: 'rgba(0, 200, 150,0.08)', '& .MuiLinearProgress-bar': { bgcolor: COLORS[i % COLORS.length], borderRadius: 2 } }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Order Status Breakdown */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #3a3d52', boxShadow: 'none', height: '100%' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#e0e0e0', mb: 2 }}>
                {t('reports.orderStatus')}
              </Typography>
              {orderStatusData.length === 0 ? (
                <Typography variant="body2" sx={{ color: '#6b7280', py: 4, textAlign: 'center' }}>{t('reports.noOrders')}</Typography>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={160}>
                    <PieChart>
                      <Pie data={orderStatusData} cx="50%" cy="50%" outerRadius={60} innerRadius={35} dataKey="value" paddingAngle={2}>
                        {orderStatusData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                      </Pie>
                      <RechartsTooltip contentStyle={{ borderRadius: 8, border: '1px solid #3a3d52', fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                    {orderStatusData.map((s, i) => (
                      <Chip
                        key={i}
                        icon={<FiberManualRecord sx={{ fontSize: '10px !important', color: `${s.color} !important` }} />}
                        label={`${s.name}: ${s.value}`}
                        size="small"
                        sx={{ height: 24, fontSize: '0.7rem', fontWeight: 500, bgcolor: alpha(s.color, 0.08), color: s.color, border: `1px solid ${alpha(s.color, 0.15)}` }}
                      />
                    ))}
                  </Box>
                </>
              )}
            </Paper>
          </Grid>

          {/* Payment Methods + Order Types */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #3a3d52', boxShadow: 'none', height: '100%' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#e0e0e0', mb: 2 }}>
                {t('reports.paymentAndFulfillment')}
              </Typography>
              {/* Payment Methods */}
              {paymentMethodData.length > 0 && (
                <Box sx={{ mb: 2.5 }}>
                  <Typography variant="caption" sx={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', mb: 1, display: 'block' }}>
                    {t('reports.paymentMethods')}
                  </Typography>
                  {paymentMethodData.map((pm, i) => (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75, borderBottom: i < paymentMethodData.length - 1 ? '1px solid #2a2d3e' : 'none' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {pm.method === 'Cash' ? <Payments sx={{ fontSize: 16, color: '#2E7D32' }} /> : pm.method === 'Card' ? <CreditCard sx={{ fontSize: 16, color: '#1565C0' }} /> : <Euro sx={{ fontSize: 16, color: '#E65100' }} />}
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#e0e0e0' }}>{pm.method}</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#e0e0e0' }}>€{pm.total.toFixed(2)}</Typography>
                        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', fontSize: '0.65rem' }}>{pm.count} orders</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
              {/* Order Types */}
              {deliveryVsPickup.length > 0 && (
                <Box>
                  <Typography variant="caption" sx={{ color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', mb: 1, display: 'block' }}>
                    {t('reports.orderTypes')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {deliveryVsPickup.map((t, i) => (
                      <Chip
                        key={i}
                        icon={t.name === 'Delivery' ? <LocalShipping sx={{ fontSize: '16px !important' }} /> : <Store sx={{ fontSize: '16px !important' }} />}
                        label={`${t.name}: ${t.value}`}
                        size="small"
                        sx={{ fontWeight: 600, fontSize: '0.72rem', bgcolor: alpha(COLORS[i % COLORS.length], 0.1), color: COLORS[i % COLORS.length] }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Peak Hours + Branch Performance */}
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #3a3d52', boxShadow: 'none' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#e0e0e0', mb: 2 }}>
                {t('reports.peakHours')} — {periodLabel}
              </Typography>
              {peakHoursData.length === 0 ? (
                <Typography variant="body2" sx={{ color: '#6b7280', py: 4, textAlign: 'center' }}>{t('reports.noPeakHoursData')}</Typography>
              ) : (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={peakHoursData} barSize={20}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#3a3d52" />
                    <XAxis dataKey="hour" tick={{ fontSize: 10, fill: '#999' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: '#999' }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <RechartsTooltip contentStyle={{ borderRadius: 8, border: '1px solid #3a3d52', fontSize: 12 }} />
                    <Bar dataKey="orders" fill="#2E7D32" radius={[4, 4, 0, 0]} name="Orders" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Paper>
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #3a3d52', boxShadow: 'none', height: '100%' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#e0e0e0', mb: 2 }}>
                {t('reports.branchPerformance')}
              </Typography>
              {branchData.length === 0 ? (
                <Typography variant="body2" sx={{ color: '#6b7280', py: 4, textAlign: 'center' }}>{t('reports.noBranchData')}</Typography>
              ) : (
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'rgba(0, 200, 150,0.08)' }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#9ca3af', py: 1 }}>{t('reports.branch').toUpperCase()}</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#9ca3af', py: 1 }} align="right">{t('reports.totalOrders').toUpperCase()}</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#9ca3af', py: 1 }} align="right">{t('reports.totalRevenue').toUpperCase()}</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#9ca3af', py: 1 }} align="right">{t('reports.avgOrderValue').toUpperCase()}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {branchData.map((b, i) => (
                        <TableRow key={i} sx={{ '&:hover': { bgcolor: 'rgba(0, 200, 150,0.08)' }, '&:last-child td': { border: 0 } }}>
                          <TableCell sx={{ fontWeight: 600, color: '#e0e0e0', py: 1, fontSize: '0.82rem' }}>{b.branch}</TableCell>
                          <TableCell align="right" sx={{ color: '#9ca3af', py: 1, fontSize: '0.82rem' }}>{b.orders}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: '#2E7D32', py: 1, fontSize: '0.82rem' }}>€{b.revenue.toFixed(2)}</TableCell>
                          <TableCell align="right" sx={{ color: '#9ca3af', py: 1, fontSize: '0.82rem' }}>€{b.orders > 0 ? (b.revenue / b.orders).toFixed(2) : '0.00'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Recent Orders Table */}
        <Paper sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #3a3d52', boxShadow: 'none' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: 2, flexWrap: 'wrap', gap: { xs: 1.5, md: 0 } }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#e0e0e0' }}>
              {t('reports.recentOrders')}
            </Typography>
            <Typography variant="caption" sx={{ color: '#9ca3af' }}>
              Last {recentOrders.length} orders — {periodLabel}
            </Typography>
          </Box>
          {recentOrders.length === 0 ? (
            <Typography variant="body2" sx={{ color: '#6b7280', py: 4, textAlign: 'center' }}>{t('reports.noOrdersInPeriod')}</Typography>
          ) : (
            <>
            {/* Mobile order cards */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1 }}>
              {recentOrders.map((order, i) => {
                const statusColor = STATUS_COLORS[order.status] || '#757575';
                return (
                  <Box key={i} sx={{ backgroundColor: '#1e2139', border: '1px solid #2a2d3e', borderRadius: '8px', p: 1.2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.85rem', fontFamily: 'monospace' }}>#{order.id}</Typography>
                      <Typography sx={{ color: '#00C896', fontWeight: 700, fontSize: '0.9rem' }}>€{order.total.toFixed(2)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <Chip label={order.status} size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600, bgcolor: alpha(statusColor, 0.12), color: statusColor }} />
                        <Typography sx={{ color: '#6b7280', fontSize: '0.65rem' }}>{order.type || 'pickup'} · {order.payment || 'cash'}</Typography>
                      </Box>
                      <Typography sx={{ color: '#6b7280', fontSize: '0.65rem' }}>{order.time.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
            {/* Desktop table */}
            <TableContainer sx={{ overflowX: 'auto', display: { xs: 'none', md: 'block' } }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'rgba(0, 200, 150,0.08)' }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#9ca3af', py: 1 }}>{t('reports.order').toUpperCase()}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#9ca3af', py: 1 }}>{t('common.status').toUpperCase()}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#9ca3af', py: 1 }}>{t('reports.type').toUpperCase()}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#9ca3af', py: 1 }}>{t('reports.payment').toUpperCase()}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#9ca3af', py: 1 }} align="right">{t('reports.items').toUpperCase()}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#9ca3af', py: 1 }} align="right">{t('reports.total').toUpperCase()}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#9ca3af', py: 1 }} align="right">{t('reports.time').toUpperCase()}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentOrders.map((order, i) => {
                    const statusColor = STATUS_COLORS[order.status] || '#757575';
                    return (
                      <TableRow key={i} sx={{ '&:hover': { bgcolor: 'rgba(0, 200, 150,0.08)' }, '&:last-child td': { border: 0 } }}>
                        <TableCell sx={{ fontWeight: 600, color: '#e0e0e0', py: 1, fontSize: '0.82rem', fontFamily: 'monospace' }}>
                          #{order.id}
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Chip
                            label={order.status}
                            size="small"
                            sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, bgcolor: alpha(statusColor, 0.1), color: statusColor, border: `1px solid ${alpha(statusColor, 0.2)}` }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                            {(order.type || 'pickup').charAt(0).toUpperCase() + (order.type || 'pickup').slice(1)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                            {(order.payment || 'cash').charAt(0).toUpperCase() + (order.payment || 'cash').slice(1)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1 }}>
                          <Typography variant="caption" sx={{ color: '#9ca3af' }}>{order.items}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#2E7D32' }}>€{order.total.toFixed(2)}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1 }}>
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>
                            {order.time.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            </>
          )}
        </Paper>
      </Box>
    </AdminLayout>
  );
};

export default ReportsPage;
